import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import ApiService from "../../../../service/ApiService";
import { apiPaths } from "../../../../service/apiPaths";

const AddEPaper: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [date, setDate] = useState<string>("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [existingDates, setExistingDates] = useState<Set<string>>(new Set());
  const [currentEPaperDate, setCurrentEPaperDate] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");

  // Fetch all existing dates to prevent duplicates
  useEffect(() => {
    const fetchExistingDates = async () => {
      try {
        const result = await ApiService({
          method: "GET",
          endpoint: apiPaths.getEPapers,
          params: { limit: 1000 }, // Get all to check dates
        });

        if (result?.success && Array.isArray(result?.data)) {
          const dates = new Set<string>();
          result.data.forEach((item: any) => {
            if (item.date) {
              const dateStr = item.date.split("T")[0]; // Extract YYYY-MM-DD
              dates.add(dateStr);
            }
          });
          setExistingDates(dates);
        }
      } catch (error) {
        console.error("Error fetching existing dates:", error);
      }
    };

    fetchExistingDates();
  }, []);

  // Load existing data when editing
  useEffect(() => {
    const fetchEPaper = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const result = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.getEPapers}/${id}`,
        });

        if (result?.success && result?.data) {
          if (result.data.date) {
            const dateStr = result.data.date.split("T")[0];
            setDate(dateStr);
            setCurrentEPaperDate(dateStr); // Store current date to allow it during edit
          }
        }
      } catch (error) {
        console.error("Error fetching E-paper:", error);
        toast.error("Failed to load E-paper");
      } finally {
        setLoading(false);
      }
    };

    fetchEPaper();
  }, [id]);

  const today = new Date().toISOString().split("T")[0];

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;
    setDate(selectedDate);
    setDateError("");

    // Validate date
    if (selectedDate) {
      if (selectedDate > today) {
        setDateError("Date cannot be in the future");
        return;
      }

      // Check for duplicate date (allow current date when editing)
      if (existingDates.has(selectedDate)) {
        if (!id || selectedDate !== currentEPaperDate) {
          setDateError(
            "An E-paper already exists for this date. Please select a different date."
          );
          return;
        }
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }
    setPdfFile(file || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date) {
      toast.error("Please select a date");
      setDateError("Please select a date");
      return;
    }

    if (date > today) {
      toast.error("Date cannot be in the future");
      setDateError("Date cannot be in the future");
      return;
    }

    // Check for duplicate date (allow current date when editing)
    if (existingDates.has(date)) {
      if (!id || date !== currentEPaperDate) {
        toast.error(
          "An E-paper already exists for this date. Please select a different date."
        );
        setDateError(
          "An E-paper already exists for this date. Please select a different date."
        );
        return;
      }
    }

    if (!id && !pdfFile) {
      toast.error("Please upload a PDF file");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("date", date);
      if (pdfFile) {
        formData.append("file", pdfFile);
      }

      const method = id ? "PUT" : "POST";
      const endpoint = id
        ? `${apiPaths.updateEPaper}/${id}`
        : apiPaths.createEPaper;

      await ApiService({
        method,
        endpoint,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      });

      toast.success(`E-paper ${id ? "updated" : "created"} successfully`);
      navigate("/e-paper");
    } catch (error) {
      console.error("Error saving E-paper:", error);
      toast.error("Failed to save E-paper");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-4 bg-white rounded-lg shadow-md mt-4">
      <h2 className="text-xl font-semibold mb-4">
        {id ? "Edit E-paper" : "Add E-paper"}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date{" "}
            {id && (
              <span className="text-gray-500 text-xs">
                (Cannot select a date that already has an E-paper)
              </span>
            )}
          </label>
          <input
            type="date"
            value={date}
            max={today}
            onChange={handleDateChange}
            className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
              dateError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-blue-500"
            }`}
          />
          {dateError && (
            <p className="text-xs text-red-500 mt-1">{dateError}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PDF File
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="w-full text-sm"
          />
          {id && !pdfFile && (
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to keep existing PDF.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-md text-white ${
              loading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Saving..." : id ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddEPaper;
